// `/` — decide which board this caller belongs on, and go there.
//
// This exists as its own route so neither board has to know about the other's audience. It
// asks `/api/me`, which returns the viewer block and nothing else: deciding that Deborah
// belongs on the activity view is a lookup against `roles.json`, and making her wait for 694KB
// of accounts she is about to navigate away from is not.
//
// `replace` rather than a push, so Back from a board goes wherever somebody came from rather
// than to a redirect that immediately fires again.

import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AccessDeniedError, fetchMe } from '../api/client';
import { useAuthContext } from '../auth/AuthContext';
import { DeniedGate } from '../auth/Gate';

export function Landing() {
  const { getToken, email, signOut } = useAuthContext();

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => fetchMe(getToken),
  });

  // Signed in and on no list at all. Nothing about either board is useful to them, so this
  // replaces the page rather than redirecting into a board that would 403 anyway.
  if (meQuery.error instanceof AccessDeniedError) {
    return <DeniedGate message={meQuery.error.message} email={email} onSignOut={signOut} />;
  }

  if (meQuery.isPending) {
    return <div className="loading">Signing you in…</div>;
  }

  // A failure here is not a reason to strand somebody on a blank page. The activity board is
  // the safe fallback: it is the default for three of the four roles, and a strategist who
  // lands there can reach their own board from the switcher in one click.
  if (meQuery.error) {
    return <Navigate to="/activity" replace />;
  }

  // An AE lands on their own accounts (GRO-360); a strategist on their board; the rest on activity.
  const defaultView = meQuery.data?.viewer.default_view;
  const destination = defaultView === 'execution' ? '/execution'
    : defaultView === 'ae' ? '/ae'
      : '/activity';
  return <Navigate to={destination} replace />;
}
